// Mirrors the W&B app's cookie and storage contract from util/admin.ts and util/forceTrace.ts.

import {LAST_NON_IMPERSONATED_ACCOUNT} from '../account/accountModel';

export const USE_ADMIN_PRIVILEGES_COOKIE = 'use_admin_privileges';
export const IMPERSONATED_USERNAME_COOKIE = 'impersonated_username';
export const FORCE_TRACE_STORAGE_KEY = 'force_trace_expires_at';

const FORCE_TRACE_DURATION_MS = 5 * 60 * 1000;
const DEBUG_VIEWER_EMAIL_SUFFIX = '@debug.wandb.com';

export function readCookie(name: string): string {
  try {
    for (const pair of document.cookie.split(';')) {
      const separator = pair.indexOf('=');
      if (separator === -1 || pair.slice(0, separator).trim() !== name) {
        continue;
      }
      return decodeURIComponent(pair.slice(separator + 1).trim());
    }
  } catch {
    return '';
  }
  return '';
}

export function viewerUsingAdminPrivileges(): boolean {
  return readCookie(USE_ADMIN_PRIVILEGES_COOKIE) === 'true';
}

function cookieDomainAttribute(): string {
  return location.hostname === 'localhost'
    ? ''
    : `; domain=${location.hostname}`;
}

export function setViewerUsingAdminPrivileges(enable: boolean): void {
  const domain = cookieDomainAttribute();
  if (enable) {
    document.cookie = `${USE_ADMIN_PRIVILEGES_COOKIE}=true; path=/; SameSite=Strict${domain}`;
    return;
  }

  document.cookie = `${USE_ADMIN_PRIVILEGES_COOKIE}=; Max-Age=0; path=/; SameSite=Strict${domain}`;
}

export function viewingAs(): string {
  return readCookie(IMPERSONATED_USERNAME_COOKIE);
}

export function stopImpersonating(): void {
  const domain = cookieDomainAttribute();
  document.cookie = `${IMPERSONATED_USERNAME_COOKIE}=; Max-Age=0; path=/; SameSite=Strict${domain}`;
  try {
    localStorage.removeItem(LAST_NON_IMPERSONATED_ACCOUNT);
  } catch {}
}

export function isInternalUserEmail(email: string | null | undefined): boolean {
  return (
    /^.+?@wandb\.com$/.test(email ?? '') ||
    /^.+?@coreweave\.com$/.test(email ?? '')
  );
}

export function getForceTraceExpiresAt(): number | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(FORCE_TRACE_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) {
    return null;
  }
  const expiresAt = Number(raw);
  return Number.isNaN(expiresAt) || Date.now() >= expiresAt ? null : expiresAt;
}

export function isForceTraceActive(viewerEmail?: string | null): boolean {
  return (
    viewerEmail?.toLowerCase().endsWith(DEBUG_VIEWER_EMAIL_SUFFIX) === true ||
    getForceTraceExpiresAt() !== null
  );
}

export function enableForceTrace(): void {
  try {
    localStorage.setItem(
      FORCE_TRACE_STORAGE_KEY,
      String(Date.now() + FORCE_TRACE_DURATION_MS)
    );
  } catch {}
}

export function disableForceTrace(): void {
  try {
    localStorage.removeItem(FORCE_TRACE_STORAGE_KEY);
  } catch {}
}
